import React from 'react';
import { SidebarLayout } from './Components/Catalyst/sidebar-layout';
import { Navbar } from './Components/Catalyst/navbar';
import { Heading, Subheading } from './Components/Catalyst/heading';
import { Divider } from './Components/Catalyst/divider';
import AppSidebar from './AppSidebar';
import LoadingSpinner from './LoadingSpinner';
import { DarkModeToggle } from './Components/Custom/DarkModeToggle';
import { ErrorAlert } from './Components/Custom/ErrorAlert';
import { formatHomeAddress, useMember } from './Hooks/MemberContext';

/**
 * The member's own details, their homes, and their appearance preference.
 *
 * Name, email, phone and addresses stay read-only: there is no
 * member-facing write endpoint for them. A member asks their association
 * manager to change an address, and the manager does it in front-porch.
 * Showing an editable form that has nowhere to submit would be worse than
 * showing the facts.
 *
 * **The password form was removed 2026-09-12, and there is nothing to put
 * in its place here.** WorkOS owns the member's credential now, so
 * `change-password` changed nothing about their ability to sign in — it
 * only bumped `token_version`, which signed them out of their other
 * sessions for no benefit. A member who wants a new password gets one
 * from AuthKit's own "forgot password" link on the sign-in page, which is
 * where the credential actually lives.
 *
 * The sidebar has linked to `/settings` and `/my-profile` since it was
 * written; neither was a route, so both fell through to `*` and bounced
 * the member to the login page.
 */
const SettingsPage: React.FC = () => {
  const { member, homes, displayName, loading, error, reload } = useMember();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <SidebarLayout
      sidebar={<AppSidebar currentPage="settings" />}
      navbar={<Navbar>{/* Your navbar content */}</Navbar>}
    >
      <Heading>Settings</Heading>
      <Divider className="mt-4 mb-6" />

      <ErrorAlert className="mb-6" onRetry={reload}>
        {error}
      </ErrorAlert>

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow p-6 mb-6">
        <Subheading>Your details</Subheading>
        <dl className="mt-4 space-y-3">
          <div className="flex justify-between">
            <dt className="text-sm font-semibold text-gray-900 dark:text-white">
              Name
            </dt>
            <dd className="text-sm text-gray-900 dark:text-white">
              {displayName || '-'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm font-semibold text-gray-900 dark:text-white">
              Email
            </dt>
            <dd className="text-sm text-gray-900 dark:text-white">
              {member?.email ?? '-'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm font-semibold text-gray-900 dark:text-white">
              Phone
            </dt>
            <dd className="text-sm text-gray-900 dark:text-white">
              {member?.phone ?? '-'}
            </dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-gray-600 dark:text-gray-300">
          To change any of these, contact your association manager.
        </p>
      </div>

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow p-6 mb-6">
        <Subheading>Signing in</Subheading>
        <p className="mt-4 text-sm text-gray-900 dark:text-white">
          Your password is managed by our sign-in provider, not here. To
          change it, sign out and use the &ldquo;Forgot your
          password?&rdquo; link on the sign-in page.
        </p>
      </div>

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow p-6 mb-6">
        <Subheading>Your {homes.length === 1 ? 'home' : 'homes'}</Subheading>
        {homes.length === 0 ? (
          <p className="mt-4 text-sm text-gray-900 dark:text-white">
            Your account is not linked to a home yet.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {homes.map((home) => (
              <li key={home.home_id} className="text-sm">
                <span className="block font-medium text-gray-900 dark:text-white">
                  {formatHomeAddress(home)}
                </span>
                <span className="block text-gray-600 dark:text-gray-300">
                  {home.hoa_name}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow p-6">
        <Subheading>Appearance</Subheading>
        <div className="mt-4">
          <DarkModeToggle />
        </div>
      </div>
    </SidebarLayout>
  );
};

export default SettingsPage;
